// =============================================================================
// io/ai/coachRun.ts — a freeform coaching read, from a brief the client builds.
// -----------------------------------------------------------------------------
// `fetchAiVerdict` asks for a STRUCTURED verdict against a schema. This asks a
// question in prose and gets prose back — the `freeform` scenario the deployed
// `ai-coach` function already supports, so nothing on the backend changes.
//
// ── 🚨 EVERY CALL IS BILLED AND BURNS A CAPPED RUN ──
//
// The same warning `aiCoach.ts` carries, and it matters more here because the
// brief is bigger. There is no automatic call anywhere in this module's callers:
// the run happens on a press, and the confirmation screen says so before the
// button is offered. Opening the view must never spend one.
//
// ── ⚠ THE FUNCTION CAPS THE PROMPT AT 32,000 CHARACTERS ──
//
// Over that it answers 413 and the run is wasted on a request that could not
// have worked. A comparison brief across a dozen accounts is nowhere near it,
// but the check is here rather than assumed, because the failure is a billed
// round trip for an error message.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { ENV } from '../../env';
import { AiCoachError, type AiErrorCode } from './aiCoach';

/** The server's own limit, mirrored so an over-long brief fails before it bills. */
export const COACH_PROMPT_MAX = 32000;

/**
 * The per-item run cap the server enforces (`COACH_ITEM_MAX`, default 2).
 *
 * ⚠ MIRRORED FOR THE COPY, NOT RELIED ON. The server is the real limit; this is
 * what the screen says out loud so "2 runs" is not a surprise. If they ever
 * disagree the server wins and the screen shows its `COACH_CAP` message.
 */
export const COACH_RUNS_PER_ITEM = 2;

export interface CoachRunResult {
  text: string;
  /** What the call cost, when the function reports it. Null otherwise. */
  costUsd: number | null;
}

export async function runFreeformCoach(
  sb: SupabaseClient,
  prompt: string,
  cacheKey: string,
  signal?: AbortSignal,
): Promise<CoachRunResult> {
  if (prompt.length > COACH_PROMPT_MAX) {
    throw new AiCoachError(
      `That brief is ${prompt.length.toLocaleString('en-US')} characters — longer than one `
      + `coaching read allows. Narrow the window or the accounts and try again.`,
      'AI_FAILED',
    );
  }

  const { data: sess } = await sb.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) {
    throw new AiCoachError('Not signed in — sign in for AI coaching.', 'AI_FAILED');
  }

  let res: Response;
  try {
    res = await fetch(`${ENV.config.supabaseUrl}/functions/v1/ai-coach`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: ENV.config.supabaseAnonKey,
      },
      body: JSON.stringify({ scenario: 'freeform', data: { prompt }, cacheKey }),
      ...(signal ? { signal } : {}),
    });
  } catch (e) {
    throw new AiCoachError(
      `The coach could not be reached: ${e instanceof Error ? e.message : String(e)}`,
      'AI_FAILED',
    );
  }

  const out = await res.json().catch(() => ({})) as Record<string, unknown>;

  if (!res.ok) {
    const code = (typeof out['errorCode'] === 'string' ? out['errorCode'] : 'AI_FAILED') as AiErrorCode;
    throw new AiCoachError(
      typeof out['error'] === 'string' ? out['error'] : `ai-coach returned ${res.status}`,
      code,
      typeof out['reason'] === 'string' ? out['reason'] : null,
    );
  }

  const result = (out['result'] ?? {}) as Record<string, unknown>;
  const text = typeof result['text'] === 'string' ? result['text'].trim() : '';
  if (!text) {
    // ⚠ NOT rendered as an empty answer. "The coach has nothing to say about
    // your accounts" is a different claim from "the reply did not arrive".
    throw new AiCoachError('The coach replied with nothing.', 'AI_FAILED');
  }
  return {
    text,
    costUsd: typeof out['costUsd'] === 'number' ? out['costUsd'] : null,
  };
}
